import { ColDef } from '@ag-grid-community/core';
import { ICONS } from '@creditsights/styleguide-web';
import { RpColumn, RpColumnRenderType } from '../../models/RpColumn';
import { IssuerListItemDto } from '../../services/dtos/issuer/issuerList.dto';
import { EsField } from '../../services/dtos/riskProducts/rpColumn.dto';

export const additionalDefaultColDef: ColDef<IssuerListItemDto> = {
  comparator: () => 0, // Prevent sort trigger before data loads when using client side data model
};
export const COLUMN_PERSISTENCE_KEY = 'companies-table-columns';
export const GRID_PAGE_SIZE = 100;

export const DEFAULT_SORT_FIELD = 'name';
export const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'asc';

export const EXCLUDED_GROUP = 'Company Details';
export const EXCLUDED_COMPANY_ES_FIELDS = [
  'rating_stability_indicator',
  'potential_rating_action',
  'cre_previous',
  'cqs_previous',
  'fscore_previous',
  'credit_risk_level_previous',
  'acr_previous',
];

export const RISK_PRODUCT_PREFIX = 'riskProducts';
export const COVERAGE_ANDOR_PERMISSION = 'ui:company_search_andor';

export const WEIGHTING_ICONS_MAP = {
  buy: ICONS.Buy,
  hold: ICONS.Hold,
  marketperform: ICONS.Marketperform,
  norecommendation: ICONS.Norecommendation,
  outperform: ICONS.Outperform,
  sell: ICONS.Sell,
  underperform: ICONS.Underperform,
};

export const DEFAULT_COMPANY_COLUMNS: EsField[] = [
  'selection',
  'name',
  'ticker',
  'sector',
  'currency',
  'creditQualities',
  'topLevelRegion',
  'country',
  'coverage',
];

export const DEFAULT_COMPANY_COLUMNS_RESEARCH: EsField[] = [
  'selection',
  'name',
  'ticker',
  'latestRecommendation.weighting',
  'latestRecommendation.changedDate',
  'latestRecommendation.publishDate',
  'sector',
  'currency',
  'creditQualities',
  'topLevelRegion',
  'country',
  'coverage',
];

export const DEFAULT_COMPANY_COLUMNS_LEVEL_2: EsField[] = [
  'selection',
  'name',
  'ticker',
  'coverage',
  'riskProducts.model_sector',
  'riskProducts.cqs',
  'riskProducts.cqs_trend_1y',
  'riskProducts.cqs_trend_3y',
  'riskProducts.cqs_outlook',
  'riskProducts.acr_level_4',
];

export const DEFAULT_COMPANY_COLUMNS_LEVEL_3: EsField[] = [
  'selection',
  'name',
  'ticker',
  'coverage',
  'riskProducts.model_sector',
  'riskProducts.cqs',
  'riskProducts.cqs_trend_1y',
  'riskProducts.cqs_trend_3y',
  'riskProducts.cqs_outlook',
  'riskProducts.acr_level_4',
  'riskProducts.cre_1y',
  'riskProducts.credit_risk_level',
];

export const COMPANY_DETAILS_COLUMNS: RpColumn[] = [
  {
    group: 'Company Details',
    data_field: '',
    es_field: 'selection',
    renderType: RpColumnRenderType.SELECTION,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Issuer/Borrower',
    colId: 'name',
    es_field: 'displayName',
    renderType: RpColumnRenderType.COMPANY_LINK,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'CS View',
    colId: 'latestRecommendation.weighting',
    es_field: 'latestRec.weighting',
    renderType: RpColumnRenderType.CS_VIEW,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Ticker',
    colId: 'ticker',
    es_field: 'displayTicker',
    renderType: RpColumnRenderType.DEFAULT,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Rec Changed',
    colId: 'latestRecommendation.changedDate',
    es_field: 'latestRec.changedDate',
    renderType: RpColumnRenderType.DATE,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Rec Reviewed',
    colId: 'latestRecommendation.publishDate',
    es_field: 'latestRec.publishDate',
    renderType: RpColumnRenderType.DATE,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Sector/Subsector',
    es_field: 'sector',
    renderType: RpColumnRenderType.IMPLIED,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Currency',
    es_field: 'currency',
    renderType: RpColumnRenderType.IMPLIED,
    meta: { sortable: false },
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Credit Grade',
    es_field: 'creditQualities',
    renderType: RpColumnRenderType.CREDIT,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Region',
    colId: 'topLevelRegion',
    es_field: 'region',
    renderType: RpColumnRenderType.IMPLIED,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Country',
    es_field: 'country',
    renderType: RpColumnRenderType.IMPLIED,
    meta: { sortable: false },
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Coverage',
    es_field: 'coverage',
    renderType: RpColumnRenderType.COVERAGE,
    canAccess: true,
  },
];

export const COMPANY_DETAILS_COLUMNS_TO_EXPORT = [
  'name',
  'latestRecommendation.weighting',
  'ticker',
  'latestRecommendation.changedDate',
  'latestRecommendation.publishDate',
  'sector',
  'currency',
  'country',
  'topLevelRegion',
  'coverage',
];

export const RP_COLUMNS_TO_EXPORT = ['cqs', 'fas', 'cre_1y', 'acr'];
